import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";

// ScrollToTop component - scrolls to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Classes from "./pages/Classes";
import ClassDetail from "./pages/ClassDetail";
import Schedule from "./pages/Schedule";
import About from "./pages/About";
import Contact from "./pages/Contact";
import FreeTrial from "./pages/FreeTrial";
import CreditBundlePayPage from "./pages/CreditBundlePayPage";
import CreditTopupPayPage from "./pages/CreditTopupPayPage";
import HighSchoolMusical from "./pages/HighSchoolMusical";
import AddCardLinkPage from "./pages/AddCardLinkPage";
import Enroll from "./pages/Enroll";
import ParentDashboard from "./pages/ParentDashboard";
import TeacherDashboard from "./pages/TeacherDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AdminMessaging from "./pages/AdminMessaging";
import AdminReports from "./pages/AdminReports";
import SiteMedia from "./pages/SiteMedia";
import MediaDrop from "./pages/MediaDrop";
import TermDates from "./pages/TermDates";
import Uniform from "./pages/Uniform";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DataDeletion from "./pages/DataDeletion";
import StudentImport from "./pages/StudentImport";
import { Toaster } from "@/components/ui/sonner";
import AccidentReports from "./pages/AccidentReports";
import ParentRegistration from "./pages/ParentRegistration";
import DailyRegister from "./pages/Register";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import AddCardPage from "./pages/AddCardPage";
import AddCardForBooking from "./pages/AddCardForBooking";
import EnrollAfterTrialPage from "./pages/EnrollAfterTrialPage";
import UserManagement from "./pages/UserManagement";
import StudentsManagement from "./pages/StudentsManagement";
import StudentDetail from "./pages/StudentDetail";
import PaymentsManagement from "./pages/PaymentsManagement";
import MailingLists from "./pages/MailingLists";
import StaffSetup from "./pages/StaffSetup";
import WestendBootcamps from "./pages/WestendBootcamps";
import BootcampAdmin from "./pages/BootcampAdmin";
import BootcampDetail from "./pages/BootcampDetail";
import BootcampPayPage from "./pages/BootcampPayPage";
import VerifyEmail from "./pages/VerifyEmail";
import Prospectus from "./pages/Prospectus";
import PublicRegister from "./pages/PublicRegister";
import Team from "./pages/Team";
import Gallery from "./pages/Gallery";
import Workshops from "./pages/Workshops";
import ShowChoir from "./pages/ShowChoir";
import Primatotterinas from "./pages/Primatotterinas";
import EmailSignatureInstall from "./pages/EmailSignatureInstall";
import CollegePartners from "./pages/CollegePartners";
import MusicalTheatreMK from "./pages/seo/MusicalTheatreMK";
import ActingMK from "./pages/seo/ActingMK";
import SingingMK from "./pages/seo/SingingMK";
import DanceMK from "./pages/seo/DanceMK";
import TheatreSchoolMK from "./pages/seo/TheatreSchoolMK";
import MobileBottomNav from "./components/ui/MobileBottomNav";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role
    if (user?.role === 'admin' || user?.role === 'staff') {
      return <Navigate to="/admin" replace />;
    }
    if (user?.role === 'teacher') {
      return <Navigate to="/teacher" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/classes" element={<Classes />} />
      <Route path="/classes/:classId" element={<ClassDetail />} />
      <Route path="/schedule" element={<Schedule />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/data-deletion" element={<DataDeletion />} />
      <Route path="/free-trial" element={<FreeTrial />} />
      <Route path="/high-school-musical" element={<HighSchoolMusical />} />
      <Route path="/hsm" element={<Navigate to="/high-school-musical" replace />} />
      <Route path="/add-card/:token" element={<AddCardLinkPage />} />
      <Route path="/credits/topup/:token" element={<CreditTopupPayPage />} />
      <Route path="/credits/:token" element={<CreditBundlePayPage />} />
      <Route path="/enroll" element={<Navigate to="/free-trial" replace />} />
      <Route path="/staff-setup" element={<StaffSetup />} />
      <Route path="/bootcamps" element={<WestendBootcamps />} />
      <Route path="/bootcamp-pay/:bookingId" element={<BootcampPayPage />} />
      <Route path="/camps" element={<WestendBootcamps />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/prospectus" element={<Prospectus />} />
      <Route path="/team" element={<Team />} />
      <Route path="/gallery" element={<Gallery />} />
      <Route path="/workshops" element={<Workshops />} />
      <Route path="/show-choir" element={<ShowChoir />} />
      <Route path="/primatotterinas" element={<Primatotterinas />} />
      <Route path="/email-signature-install" element={<EmailSignatureInstall />} />
      <Route path="/college-partners" element={<CollegePartners />} />

      {/* SEO Landing Pages — Milton Keynes long-tail keywords */}
      <Route path="/musical-theatre-classes-milton-keynes" element={<MusicalTheatreMK />} />
      <Route path="/acting-classes-milton-keynes" element={<ActingMK />} />
      <Route path="/singing-classes-milton-keynes" element={<SingingMK />} />
      <Route path="/dance-classes-milton-keynes" element={<DanceMK />} />
      <Route path="/theatre-school-milton-keynes" element={<TheatreSchoolMK />} />
      
      {/* Payment Routes */}
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/add-card" element={<AddCardPage />} />
      <Route path="/add-card-for-booking" element={<AddCardForBooking />} />
      <Route path="/enroll-after-trial" element={<EnrollAfterTrialPage />} />
      
      {/* Parent Registration (from invite link) */}
      <Route path="/register" element={<ParentRegistration />} />
      
      {/* Quick Signup (for enrollment flow) */}
      <Route path="/signup" element={<Signup />} />
      
      {/* Public Registration - anyone can sign up */}
      <Route path="/public-signup" element={<PublicRegister />} />
      
      {/* Parent/Student Dashboard */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['parent']}>
            <ParentDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Teacher Dashboard */}
      <Route 
        path="/teacher" 
        element={
          <ProtectedRoute allowedRoles={['teacher']}>
            <TeacherDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Dashboard Routes */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/classes" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/schedules" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/terms" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/rooms" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/bookings" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/waitlist" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/finance" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/users" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <UserManagement />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Trials Tab */}
      <Route 
        path="/admin/trials" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin No-Shows */}
      <Route 
        path="/admin/no-shows" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Email Templates */}
      <Route 
        path="/admin/email-templates" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin VAT Threshold */}
      <Route 
        path="/admin/vat" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Email Logs */}
      <Route 
        path="/admin/email-logs" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Contact Submissions */}
      <Route 
        path="/admin/contact" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Facebook Leads */}
      <Route 
        path="/admin/fb-leads" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Trial Comms (email + WhatsApp templates) */}
      <Route 
        path="/admin/trial-comms" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      

      {/* Admin College Access */}
      <Route 
        path="/admin/college" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Page Content Editor */}
      <Route 
        path="/admin/pages" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Data Cleanup */}
      <Route 
        path="/admin/data-cleanup" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/admin/data-integrity" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/admin/data-export" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Renewals */}
      <Route 
        path="/admin/renewals" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />

      {/* Admin PAYG Credit Bundles */}
      <Route
        path="/admin/credit-bundles"
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Admin Messaging */}
      <Route 
        path="/admin/messages" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminMessaging />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Reports */}
      <Route 
        path="/admin/reports" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminReports />
          </ProtectedRoute>
        } 
      />
      
      {/* Mailing Lists */}
      <Route 
        path="/admin/mailing-lists" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Discounts & Scholarships */}
      <Route 
        path="/admin/discounts" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Bootcamp Admin */}
      <Route 
        path="/admin/bootcamps" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <BootcampAdmin />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/bootcamps/:id" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <BootcampDetail />
          </ProtectedRoute>
        } 
      />
      
      {/* Site Media Management */}
      <Route 
        path="/admin/site-media" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <SiteMedia />
          </ProtectedRoute>
        } 
      />
      
      {/* Term Dates - All logged in users */}
      <Route 
        path="/term-dates" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff', 'teacher', 'parent']}>
            <TermDates />
          </ProtectedRoute>
        } 
      />
      
      {/* Uniform - All logged in users */}
      <Route 
        path="/uniform" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff', 'teacher', 'parent']}>
            <Uniform />
          </ProtectedRoute>
        } 
      />
      
      {/* Daily Register - Admin, Staff, Teachers */}
      <Route 
        path="/daily-register" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff', 'teacher']}>
            <DailyRegister />
          </ProtectedRoute>
        } 
      />
      
      {/* Media Drop - Admin, Staff, Teachers */}
      <Route 
        path="/media-drop" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff', 'teacher']}>
            <MediaDrop />
          </ProtectedRoute>
        } 
      />
      
      {/* Student Import - Admin, Staff only */}
      <Route 
        path="/admin/students/import" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <StudentImport />
          </ProtectedRoute>
        } 
      />
      
      {/* Students Management - Admin, Staff only */}
      <Route 
        path="/admin/students" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <StudentsManagement />
          </ProtectedRoute>
        } 
      />
      
      {/* Student Detail - Admin, Staff only */}
      <Route 
        path="/admin/students/:studentId" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <StudentDetail />
          </ProtectedRoute>
        } 
      />
      
      {/* Payments Management - Admin, Staff only */}
      <Route 
        path="/admin/payments" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <PaymentsManagement />
          </ProtectedRoute>
        } 
      />
      
      {/* Pending Payments (Take Payments) - Admin, Staff only */}
      <Route 
        path="/admin/pending-payments" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard defaultTab="pending-payments" />
          </ProtectedRoute>
        } 
      />
      
      {/* Registration Progress - Admin, Staff only */}
      <Route 
        path="/admin/registration-progress" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff']}>
            <AdminDashboard defaultTab="registration-progress" />
          </ProtectedRoute>
        } 
      />
      
      {/* Accident Reports - All staff roles and parents */}
      <Route 
        path="/accident-reports" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'staff', 'teacher', 'parent']}>
            <AccidentReports />
          </ProtectedRoute>
        } 
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <AppRoutes />
          <MobileBottomNav />
        </AuthProvider>
      </BrowserRouter>
      {/* Global toast container — required for sonner toast() calls to render anywhere */}
      <Toaster richColors position="top-right" closeButton />
    </div>
  );
}

export default App;
